import { alpha, Theme } from "@mui/material";
import { CellRendererSelectorResult, CellStyleFunc, ColDef, ColGroupDef, ICellRendererParams, ValueFormatterParams } from "ag-grid-community";

import { Version } from "fond/types";
import { formatCurrency } from "fond/utils/currency";
import { numberValueFormatter } from "fond/widgets/AgGrid";

import { ComparisonRowData } from "./buildComparisonRowData";
import { getCellDiff } from "./comparisonDiff";
import { DiffCellRenderer } from "./DiffCellRenderer";
import { GrandTotalLabel } from "./GrandTotalLabel";

// Define each column's flex ratio, so proportions hold whether pinned to these minimums or
// stretched to fill space. The banner syncs its own widths separately (see BomComparisonTable.tsx's
// syncBannerWidths) rather than reading these constants directly.
export const ITEM_COLUMN_WIDTH = 160;
export const DESCRIPTION_COLUMN_WIDTH = 280;
export const QUANTITY_COLUMN_WIDTH = 90;
export const TOTAL_COST_COLUMN_WIDTH = 150;
export const VERSION_COLUMN_WIDTH = QUANTITY_COLUMN_WIDTH + TOTAL_COST_COLUMN_WIDTH;

export type ComparisonGridRow = ComparisonRowData & { isGrandTotal?: boolean };

export const totalColId = (versionId: string): string => `total_${versionId}`;
export const countColId = (versionId: string): string => `count_${versionId}`;
export const GRAND_TOTAL_ROW_ID = "grand-total";

export const getRowId = (data: ComparisonGridRow): string => (data.isGrandTotal ? GRAND_TOTAL_ROW_ID : data.path.join("-"));

// Rules measure different units, so a summed quantity is meaningless for group/grand-total rows.
const quantityValueFormatter = (params: ValueFormatterParams<ComparisonGridRow>) =>
  params.node?.group || params.data?.isGrandTotal ? "—" : numberValueFormatter(params);

const totalCostValueFormatter =
  (currency: string | undefined) =>
  (params: ValueFormatterParams<ComparisonGridRow>): string =>
    formatCurrency(params.value, { currency });

const autoGroupCellRendererSelector = (params: ICellRendererParams<ComparisonGridRow>): CellRendererSelectorResult | undefined =>
  params.data?.isGrandTotal ? { component: GrandTotalLabel } : undefined;

const autoGroupCellClass = (params: { data?: ComparisonGridRow }): string | undefined => (params.data?.isGrandTotal ? "vertically-align" : undefined);

export function buildAutoGroupColumnDef(): ColDef<ComparisonGridRow> {
  return {
    headerName: "Item",
    minWidth: ITEM_COLUMN_WIDTH,
    flex: ITEM_COLUMN_WIDTH,
    wrapText: true,
    autoHeight: true,
    colSpan: (params) => (params.data?.isGrandTotal ? 2 : 1),
    cellClass: autoGroupCellClass,
    cellRenderer: "agGroupCellRenderer",
    cellRendererParams: {
      suppressCount: true,
      innerRenderer: (params: { value?: string }) => params.value ?? "",
    },
    cellRendererSelector: autoGroupCellRendererSelector,
  };
}

interface BuildColumnDefsArgs {
  versions: Version[];
  baselineVersion: Version;
  currency?: string;
  theme: Theme;
}

export function buildComparisonColumnDefs({
  versions,
  baselineVersion,
  currency,
  theme,
}: BuildColumnDefsArgs): (ColDef<ComparisonGridRow> | ColGroupDef<ComparisonGridRow>)[] {
  const baselineTotalColId = totalColId(baselineVersion.ID);

  const descriptionColumn: ColDef<ComparisonGridRow> = {
    field: "description",
    headerName: "Description",
    minWidth: DESCRIPTION_COLUMN_WIDTH,
    flex: DESCRIPTION_COLUMN_WIDTH,
    wrapText: true,
    autoHeight: true,
    headerClass: "centered-header",
    cellClass: "centered-cell",
  };

  const versionColumns: ColGroupDef<ComparisonGridRow>[] = versions.map((version, index) => {
    const isBaseline = version.ID === baselineVersion.ID;

    return {
      headerName: isBaseline ? "Baseline" : `Scenario ${index}`,
      headerClass: "bom-version-group-header",
      children: [
        {
          colId: countColId(version.ID),
          field: `values.${version.ID}.count`,
          headerName: "Quantity",
          minWidth: QUANTITY_COLUMN_WIDTH,
          flex: QUANTITY_COLUMN_WIDTH,
          valueFormatter: quantityValueFormatter,
          headerClass: "centered-header",
          cellClass: "vertically-align centered-cell",
        },
        {
          colId: totalColId(version.ID),
          field: `values.${version.ID}.total`,
          headerName: "Total cost",
          minWidth: TOTAL_COST_COLUMN_WIDTH,
          flex: TOTAL_COST_COLUMN_WIDTH,
          aggFunc: "sum",
          valueFormatter: totalCostValueFormatter(currency),
          headerClass: "centered-header",
          cellClass: "vertically-align centered-cell",
          ...(isBaseline
            ? {}
            : {
                cellRenderer: DiffCellRenderer,
                cellRendererParams: { baselineColId: baselineTotalColId },
                cellStyle: ((params) => {
                  const diff = getCellDiff(params.value, params.api, params.node, baselineTotalColId);
                  return diff?.isSignificant
                    ? { backgroundColor: alpha(diff.isIncrease ? theme.palette.error.main : theme.palette.success.main, 0.12) }
                    : undefined;
                }) satisfies CellStyleFunc<ComparisonGridRow, number>,
              }),
        },
      ],
    };
  });

  return [descriptionColumn, ...versionColumns];
}
